import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faCamera, faListCheck } from '@fortawesome/free-solid-svg-icons';
import './taskListEditor.css';

/**
 * Shared editor for a "desired task list" — used by the property modals, the
 * booking modals, and the all-units Tasks page. Fully controlled:
 *   tasks:     [{ id?, instruction, is_report_required }]
 *   onChange:  (nextTasks) => void
 *   photoTasks (optional): read-only [{ id, instruction, photo_url }] — tasks
 *              auto-created from setup pictures. Shown as locked rows so the
 *              client sees them but manages them via the setup photos, not here.
 */
export default function TaskListEditor({
    tasks = [],
    onChange,
    photoTasks = [],
    addLabel = 'Add a task',
    emptyHint = 'No tasks yet. Add instructions the cleaner should follow every visit.',
    disabled = false,
}) {
    const update = (idx, patch) => {
        const next = tasks.map((t, i) => (i === idx ? { ...t, ...patch } : t));
        onChange(next);
    };
    const remove = (idx) => onChange(tasks.filter((_, i) => i !== idx));
    const add = () => onChange([...tasks, { id: null, instruction: '', is_report_required: false }]);

    return (
        <div className="tle">
            {photoTasks.length > 0 && (
                <div className="tle__photo-group">
                    <div className="tle__photo-hint">
                        <FontAwesomeIcon icon={faCamera} /> Auto-created from your setup pictures — the cleaner
                        photographs these each visit. Manage them in the Setup pictures section above.
                    </div>
                    {photoTasks.map((pt) => (
                        <div className="tle__row tle__row--photo" key={`photo-${pt.id}`}>
                            {pt.photo_url
                                ? <img className="tle__thumb" src={pt.photo_url} alt="" />
                                : <span className="tle__thumb tle__thumb--empty"><FontAwesomeIcon icon={faCamera} /></span>}
                            <div className="tle__photo-instr">{pt.instruction || 'Photo at each cleaning'}</div>
                            <span className="tle__badge tle__badge--photo">Photo each visit</span>
                        </div>
                    ))}
                </div>
            )}

            {tasks.length === 0 && photoTasks.length === 0 && (
                <div className="tle__empty"><FontAwesomeIcon icon={faListCheck} /> {emptyHint}</div>
            )}

            {tasks.map((t, idx) => (
                <div className="tle__row" key={t.id != null ? `t-${t.id}` : `n-${idx}`}>
                    <textarea
                        className="tle__input"
                        rows={1}
                        placeholder="e.g. Set the AC to 24°C and close all curtains"
                        value={t.instruction}
                        disabled={disabled}
                        onChange={(e) => update(idx, { instruction: e.target.value })}
                    />
                    <label className="tle__report" title="Ask the cleaner to upload a photo proving this was done">
                        <input
                            type="checkbox"
                            checked={!!t.is_report_required}
                            disabled={disabled}
                            onChange={(e) => update(idx, { is_report_required: e.target.checked })}
                        />
                        <FontAwesomeIcon icon={faCamera} /> Request photo report
                    </label>
                    <button type="button" className="tle__remove" disabled={disabled}
                        onClick={() => remove(idx)} aria-label="Remove task">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            ))}

            <button type="button" className="tle__add" disabled={disabled} onClick={add}>
                <FontAwesomeIcon icon={faPlus} /> {addLabel}
            </button>
        </div>
    );
}
